import type { DsFilter, TopicNode as TopicNodeModel } from '../types';
import { DRONE_SCOPED_FILTERS } from '../utils/filterDefs';
import { TopicNode } from './TopicNode';
import { TopicSearch } from './TopicSearch';

interface Props {
  tree: TopicNodeModel[];
  leafCount: number;
  /** Topics that have actually received a message (≤ leafCount). When fewer
   *  than leafCount, the rest are subscribed-but-silent placeholders. */
  liveCount: number;
  search: string;
  onSearch: (v: string) => void;
  selectedPath: string | null;
  onSelect: (path: string) => void;
  /** Active filter chip — drives the "listening, no data" hint copy.
   *  Defaults to 'all' for reuse outside the Data Stream (e.g. Debug panel). */
  filter?: DsFilter;
  /** Drones currently reporting telemetry — a per-drone filter with 0 of these
   *  is "waiting for a drone", not "no topic". */
  activeDroneCount?: number;
  /** True when the topic-name search is narrowing the tree. */
  searching?: boolean;
}

const FILTER_NOUN: Record<DsFilter, string> = {
  all: 'topics',
  aircraft: 'aircraft topics',
  surveillance: 'surveillance topics',
  traffic: 'traffic topics',
  events: 'per-drone Events topics',
  alerts: 'Alerts topics',
  oi: 'per-drone Operational Intent topics',
};

/** Explain an empty/silent filter result: we ARE subscribed, just no data yet —
 *  and if it's a per-drone family with no active drones, say so explicitly. */
function ListeningBanner({
  filter,
  leafCount,
  activeDroneCount,
}: {
  filter: DsFilter;
  leafCount: number;
  activeDroneCount: number;
}) {
  const noun = FILTER_NOUN[filter];
  const subscribed = leafCount > 0;
  const droneScoped = DRONE_SCOPED_FILTERS.has(filter);

  return (
    <div className="m-2 rounded-md border border-(--color-border) bg-(--color-surface) p-3">
      <div className="flex items-center gap-2">
        <span className="size-2 shrink-0 animate-pulse rounded-full bg-(--color-amber)" />
        <span className="font-sans text-[0.808em] font-semibold text-(--color-text)">
          Listening — no data yet
        </span>
      </div>
      <p className="mt-1.5 font-sans text-[0.769em] leading-snug text-(--color-text3)">
        {subscribed
          ? `Subscribed to ${noun}, but the broker hasn't published any messages on them yet.`
          : `No ${noun} have arrived on the broker yet.`}
        {droneScoped &&
          (activeDroneCount === 0
            ? ' These are per-drone — they appear once a drone comes online (no active drones right now).'
            : ` ${activeDroneCount} drone${activeDroneCount === 1 ? '' : 's'} online, none publishing on these topics yet.`)}
      </p>
    </div>
  );
}

export function TopicTreePane({
  tree,
  leafCount,
  liveCount,
  search,
  onSearch,
  selectedPath,
  onSelect,
  filter = 'all',
  activeDroneCount = 0,
  searching = false,
}: Props) {
  // "Live" means a real message has landed. 0 live → we're subscribed but the
  // topic(s) are silent; show a meaningful reason instead of a blank tree.
  const showListening = liveCount === 0 && !searching;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex items-center gap-2 border-b border-(--color-border) bg-(--color-surface) px-3 py-[7px]">
        <span className="flex-1 font-sans text-[1em] font-bold text-(--color-text)">Topics</span>
        <span
          className="font-mono text-[0.692em] text-(--color-text3)"
          data-testid="ds-topic-count"
          title={
            liveCount < leafCount
              ? `${liveCount} live, ${leafCount - liveCount} subscribed but silent`
              : undefined
          }
        >
          {liveCount < leafCount ? `${liveCount} / ${leafCount} topics` : `${leafCount} topics`}
        </span>
      </div>
      <TopicSearch value={search} onChange={onSearch} />
      <div className="flex-1 overflow-y-auto">
        {showListening && (
          <ListeningBanner
            filter={filter}
            leafCount={leafCount}
            activeDroneCount={activeDroneCount}
          />
        )}
        {tree.map((n) => (
          <TopicNode
            key={n.path}
            node={n}
            depth={0}
            selectedPath={selectedPath}
            onSelect={onSelect}
            forceOpen={searching}
          />
        ))}
        {tree.length === 0 &&
          !showListening &&
          (searching ? (
            <div className="p-3 font-sans text-[0.769em] text-(--color-text3)">
              No topic name matches <span className="font-bold">“{search.trim()}”</span>.
            </div>
          ) : (
            <div className="p-3 font-sans text-[0.769em] italic text-(--color-text3)">
              No topics seen yet…
            </div>
          ))}
      </div>
    </div>
  );
}
